import React from 'react'
import { Navigate, type RouteObject } from 'react-router-dom'
import { lazyImport } from 'shared/lib'
import { MainLayout } from 'app/layouts/MainLayout/MainLayout'
import { ProtectedRoute } from 'shared/auth'
import { CmsLayout } from 'features/cms/shared'

const { DashboardPage } = lazyImport(
    () => import('pages/dashboard'),
    'DashboardPage'
)

const { UserProfilePage } = lazyImport(
    () =>
        import('pages/user-profile') as unknown as Promise<{
            UserProfilePage: React.ComponentType<unknown>
        }>,
    'UserProfilePage'
)

const { SeasonPage } = lazyImport(
    () => import('pages/seasons/ui/Page/Page'),
    'SeasonPage'
)

const { OrganizationPage } = lazyImport(
    () => import('pages/organizations/ui/Page/Page'),
    'OrganizationPage'
)

const { UsersPage } = lazyImport(() => import('pages/users'), 'UsersPage')

const { UnitsPage } = lazyImport(() => import('pages/units'), 'UnitsPage')

const { SponsorsPage } = lazyImport(
    () => import('pages/sponsors'),
    'SponsorsPage'
)

const { SportPage } = lazyImport(() => import('pages/sports'), 'SportPage')

const { RecreationPage } = lazyImport(
    () => import('pages/recreations'),
    'RecreationPage'
)

const { AccreditationPage } = lazyImport(
    () => import('pages/accreditation'),
    'AccreditationPage'
)

const { CompetitionPage } = lazyImport(
    () => import('pages/competition'),
    'CompetitionPage'
)

const { LeaguesPage } = lazyImport(() => import('pages/leagues'), 'LeaguesPage')

const { RolesPage } = lazyImport(() => import('pages/roles'), 'RolesPage')

// CMS pages
const { CmsPublicationsPage } = lazyImport(
    () => import('pages/cms/publications/ui/Page/Page'),
    'CmsPublicationsPage'
)
const { CmsPublicationFormPage } = lazyImport(
    () => import('pages/cms/publication-form/ui/Page/Page'),
    'CmsPublicationFormPage'
)
const { CmsEventsPage } = lazyImport(
    () => import('pages/cms/events/ui/Page/Page'),
    'CmsEventsPage'
)
const { CmsEventFormPage } = lazyImport(
    () => import('pages/cms/event-form/ui/Page/Page'),
    'CmsEventFormPage'
)
const { CmsPagesPage } = lazyImport(
    () => import('pages/cms/pages/ui/Page/Page'),
    'CmsPagesPage'
)
const { CmsPageFormPage } = lazyImport(
    () => import('pages/cms/page-form/ui/Page/Page'),
    'CmsPageFormPage'
)
const { CmsDocumentsPage } = lazyImport(
    () => import('pages/cms/documents/ui/Page/Page'),
    'CmsDocumentsPage'
)
const { CmsDocumentCategoryPage } = lazyImport(
    () => import('pages/cms/document-category/ui/Page/Page'),
    'CmsDocumentCategoryPage'
)
const { CmsSettingsPage } = lazyImport(
    () => import('pages/cms/settings/ui/Page/Page'),
    'CmsSettingsPage'
)
const { CmsSponsorsPage } = lazyImport(
    () => import('pages/cms/sponsors/ui/Page/Page'),
    'CmsSponsorsPage'
)
const { CmsMembersPage } = lazyImport(
    () => import('pages/cms/members/ui/Page/Page'),
    'CmsMembersPage'
)
const { CmsInstitutionsPage } = lazyImport(
    () => import('pages/cms/institutions/ui/Page/Page'),
    'CmsInstitutionsPage'
)

const { CmsPublicationCategoriesPage } = lazyImport(
    () => import('pages/cms/publication-categories/ui/Page/Page'),
    'CmsPublicationCategoriesPage'
)

const { CmsCompetitionTypesPage } = lazyImport(
    () => import('pages/cms/competition-types/ui/Page/Page'),
    'CmsCompetitionTypesPage'
)

const { SportImagesPageRoute: CmsSportImagesPage } = lazyImport(
    () => import('pages/cms/sport/ui/SportImagesPageRoute'),
    'SportImagesPageRoute'
)

export const protectedRoutes: RouteObject[] = [
    {
        element: <MainLayout />,
        children: [
            {
                path: '/korisnicki-profil',
                element: <UserProfilePage />,
            },
            {
                path: '/sezone',
                element: (
                    <ProtectedRoute action="read" subject="Seasons">
                        <SeasonPage />
                    </ProtectedRoute>
                ),
            },
            {
                path: '/korisnici',
                element: (
                    <ProtectedRoute action="read" subject="Users">
                        <UsersPage />
                    </ProtectedRoute>
                ),
            },
            {
                path: '/organizacije',
                element: (
                    <ProtectedRoute action="read" subject="Organizations">
                        <OrganizationPage />
                    </ProtectedRoute>
                ),
            },
            {
                path: '/sastavnice',
                element: (
                    <ProtectedRoute action="read" subject="OrganizationUnits">
                        <UnitsPage />
                    </ProtectedRoute>
                ),
            },
            {
                path: '/sponzori',
                element: (
                    <ProtectedRoute action="read" subject="Sponsors">
                        <SponsorsPage />
                    </ProtectedRoute>
                ),
            },
            {
                path: '/lige',
                element: (
                    <ProtectedRoute action="read" subject="Leagues">
                        <LeaguesPage />
                    </ProtectedRoute>
                ),
            },
            {
                path: '/sportovi',
                element: (
                    <ProtectedRoute action="read" subject="Sports">
                        <SportPage />
                    </ProtectedRoute>
                ),
            },
            {
                path: '/akreditacije',
                element: (
                    <ProtectedRoute action="read" subject="Accreditations">
                        <AccreditationPage />
                    </ProtectedRoute>
                ),
            },
            {
                path: '/roles',
                element: (
                    <ProtectedRoute action="read" subject="RolesAndPermissions">
                        <RolesPage />
                    </ProtectedRoute>
                ),
            },
            {
                path: '/natjecanja',
                element: (
                    <ProtectedRoute action="read" subject="Competition">
                        <CompetitionPage />
                    </ProtectedRoute>
                ),
            },
            {
                path: '/rekreacije',
                element: (
                    <ProtectedRoute action="read" subject="Recreation">
                        <RecreationPage />
                    </ProtectedRoute>
                ),
            },
            // CMS routes — wrapped in CmsLayout which provides CmsOrgContext
            {
                path: '/cms',
                element: (
                    <ProtectedRoute action="read" subject="CMSMenu">
                        <CmsLayout />
                    </ProtectedRoute>
                ),
                children: [
                    {
                        path: 'novosti',
                        element: <CmsPublicationsPage />,
                    },
                    {
                        path: 'novosti/new',
                        element: <CmsPublicationsPage />,
                    },
                    {
                        path: 'novosti/:id',
                        element: <CmsPublicationFormPage />,
                    },
                    {
                        path: 'dogadanja',
                        element: <CmsEventsPage />,
                    },
                    {
                        path: 'dogadanja/new',
                        element: <CmsEventsPage />,
                    },
                    {
                        path: 'dogadanja/:id',
                        element: <CmsEventFormPage />,
                    },
                    {
                        path: 'stranice',
                        element: <CmsPagesPage />,
                    },
                    {
                        path: 'stranice/new',
                        element: <CmsPagesPage />,
                    },
                    {
                        path: 'stranice/:id',
                        element: <CmsPageFormPage />,
                    },
                    {
                        path: 'kategorije',
                        element: <CmsPublicationCategoriesPage />,
                    },
                    {
                        path: 'dokumenti',
                        element: <CmsDocumentsPage />,
                    },
                    {
                        path: 'dokumenti/:id',
                        element: <CmsDocumentCategoryPage />,
                    },
                    {
                        path: 'postavke',
                        element: (
                            <ProtectedRoute action="read" subject="CmsSettings">
                                <CmsSettingsPage />
                            </ProtectedRoute>
                        ),
                    },
                    {
                        path: 'sponzori',
                        element: (
                            <ProtectedRoute action="read" subject="CmsSponsors">
                                <CmsSponsorsPage />
                            </ProtectedRoute>
                        ),
                    },
                    {
                        path: 'clanovi',
                        element: (
                            <ProtectedRoute action="read" subject="CmsMembers">
                                <CmsMembersPage />
                            </ProtectedRoute>
                        ),
                    },
                    {
                        path: 'clanice',
                        element: (
                            <ProtectedRoute
                                action="read"
                                subject="CmsInstitutions"
                            >
                                <CmsInstitutionsPage />
                            </ProtectedRoute>
                        ),
                    },
                    {
                        path: 'tipovi-natjecanja',
                        element: (
                            <ProtectedRoute
                                action="read"
                                subject="CmsCompetitionTypes"
                            >
                                <CmsCompetitionTypesPage />
                            </ProtectedRoute>
                        ),
                    },
                    {
                        path: 'sport',
                        element: (
                            <ProtectedRoute
                                action="read"
                                subject="CmsSportsImages"
                            >
                                <CmsSportImagesPage />
                            </ProtectedRoute>
                        ),
                    },
                ],
            },
            {
                path: '/dashboard',
                element: <DashboardPage />,
            },
            {
                path: '*',
                element: <Navigate to="/korisnicki-profil" replace />,
            },
        ],
    },
]
